/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Logo } from "@/components/brand/logo";
import { EmailCaptureForm } from "@/components/landing/email-capture-form";
import { EMAILS } from "@/lib/constants/master";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { Suspense } from "react";

const footerLinks = {
  product: [
    { name: "Pricing", href: "/pricing" },
    { name: "Compare", href: "/compare" },
    { name: "Integrations", href: "/integrations" },
    { name: "Roadmap", href: "/roadmap" },
    { name: "Free Calculators", href: "/tools" },
  ],
  features: [
    { name: "Estimates & Takeoffs", href: "/features/estimates" },
    { name: "Proposals & Portal", href: "/features/proposals-portal" },
    { name: "Change Orders", href: "/features/change-orders" },
    { name: "Projects & Services", href: "/features/project-management" },
    { name: "Schedule & Dispatch", href: "/features/schedule" },
    { name: "Field Ops & Time Tracking", href: "/features/field-ops" },
    { name: "Purchase Orders", href: "/features/purchase-orders" },
    { name: "Subcontractors", href: "/features/subcontractors" },
    { name: "Budgets & Invoices", href: "/features/budgets-invoices" },
    { name: "Reporting & Analytics", href: "/features/reporting" },
    { name: "Cost Database", href: "/features/cost-database" },
    { name: "Client Portal", href: "/features/client-portal" },
    { name: "Divisions", href: "/features/divisions" },
    { name: "AI Assistant", href: "/features/ai-assistant" },
    { name: "Documents", href: "/features/documents" },
    { name: "CRM", href: "/features/crm" },
    { name: "Team & Permissions", href: "/features/collaboration" },
    { name: "Calendar", href: "/features/calendar" },
    { name: "Communications", href: "/features/email-hub" },
    { name: "All Features", href: "/features" },
  ],
  solutions: [
    { name: "Landscaping", href: "/solutions/landscaping" },
    { name: "HVAC", href: "/solutions/hvac" },
    { name: "Electrical", href: "/solutions/electrical" },
    { name: "Plumbing", href: "/solutions/plumbing" },
    { name: "Roofing", href: "/solutions/roofing" },
    { name: "Concrete", href: "/solutions/concrete" },
    { name: "General Contracting", href: "/solutions/general-contracting" },
    { name: "Residential", href: "/solutions/residential-construction" },
  ],
  company: [
    { name: "About", href: "/about" },
    { name: "Contact", href: `mailto:${EMAILS.CONTACT}` },
    { name: "Support", href: "/app/support" },
    { name: "Blog", href: "/blog" },
    { name: "Resource Library", href: "/resources" },
    { name: "Docs", href: "https://docs.contractor-co-pilot.com" },
    { name: "Glossary", href: "/glossary" },
    { name: "Templates", href: "/templates" },
    { name: "System status", href: "/status" },
  ],
};

const legalLinks = [
  { name: "Terms", href: "/terms" },
  { name: "Privacy", href: "/privacy" },
  { name: "Cookies", href: "/cookies" },
  { name: "Acceptable Use", href: "/legal/acceptable-use" },
  { name: "Sub-processors", href: "/legal/sub-processors" },
];

function RedditIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm6.066 13.066c.1.333.158.683.158 1.034 0 3.37-3.916 6.1-8.748 6.1S.728 17.47.728 14.1c0-.351.058-.7.157-1.034a1.75 1.75 0 1 1 2.275-2.32 9.568 9.568 0 0 1 5.14-1.622l.97-4.56a.392.392 0 0 1 .468-.303l3.23.687a1.25 1.25 0 1 1-.14.674l-2.9-.617-.862 4.063a9.532 9.532 0 0 1 5.065 1.618 1.75 1.75 0 1 1 2.275 2.32h-.34zM9.6 14.5a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm4.8 0a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5zm-4.65 2.95a.36.36 0 0 1 0-.51.36.36 0 0 1 .51 0c.617.618 1.534.724 2.14.724s1.523-.106 2.14-.724a.36.36 0 0 1 .51 0 .36.36 0 0 1 0 .51c-.79.79-1.956.898-2.65.898s-1.86-.108-2.65-.898z" />
    </svg>
  );
}

const socialLinks = [
  { name: "X (Twitter)", icon: Twitter, href: "https://x.com/contractor_ai" },
  {
    name: "LinkedIn",
    icon: Linkedin,
    href: "https://www.linkedin.com/company/contractor-co-pilot",
  },
  {
    name: "Facebook",
    icon: Facebook,
    href: "https://facebook.com/contractorcopilot",
  },
  {
    name: "Instagram",
    icon: Instagram,
    href: "https://instagram.com/contractorcopilot",
  },
  {
    name: "Reddit",
    icon: RedditIcon,
    href: "https://reddit.com/r/contractorcopilot",
  },
];

function LinkColumn({
  title,
  links,
}: {
  title: string;
  links: { name: string; href: string }[];
}) {
  return (
    <div className="space-y-3">
      <h3 className="text-primary-foreground font-bold text-xs uppercase tracking-wider">
        {title}
      </h3>
      <ul className="space-y-2">
        {links.map((link) => (
          <li key={link.name}>
            <a
              href={link.href}
              className="text-sm text-primary-foreground/90 transition-colors hover:text-primary-foreground py-2 min-h-[44px] inline-flex items-center"
            >
              {link.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-20 pt-10 pb-8">
        {/* Five columns: Product, Features, Solutions, Company, Subscribe */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8 mb-8">
          <LinkColumn title="Product" links={footerLinks.product} />
          <LinkColumn title="Features" links={footerLinks.features} />
          <LinkColumn title="Solutions" links={footerLinks.solutions} />
          <LinkColumn title="Company" links={footerLinks.company} />

          {/* Subscribe column */}
          <div className="space-y-3 col-span-2 sm:col-span-3 lg:col-span-1">
            <h3 className="text-primary-foreground font-bold text-xs uppercase tracking-wider">
              Subscribe
            </h3>
            <Suspense fallback={<div className="h-8" />}>
              <EmailCaptureForm
                buttonText="Subscribe"
                variant="inline"
                inputClassName="bg-white text-foreground placeholder:text-muted-foreground rounded-md"
                buttonClassName="rounded-md"
              />
            </Suspense>
            <div className="rounded-md bg-primary-foreground/10 border border-primary-foreground/15 p-3 mt-3">
              <p className="text-xs text-primary-foreground/90 leading-relaxed">
                The business platform for contractors. Simple. Connected.
                Pre-loaded for your trade.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom bar: Logo | Legal | Social */}
        <div className="border-t border-primary-foreground/20 pt-5 md:pt-6 flex flex-col md:flex-row justify-between items-center gap-3 md:gap-5">
          <div className="flex items-center gap-2">
            <Logo size="md" className="text-primary-foreground" />
            <span className="text-[10px] font-medium uppercase tracking-wider text-primary-foreground/50 border border-primary-foreground/20 rounded px-1.5 py-0.5">
              Early Access
            </span>
          </div>
          <nav
            className="flex flex-wrap justify-center gap-4 md:gap-4 text-sm text-primary-foreground/90"
            aria-label="Legal"
          >
            {legalLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="transition-colors hover:text-primary-foreground py-2 min-h-[44px] inline-flex items-center"
              >
                {link.name}
              </a>
            ))}
          </nav>
          <div className="flex gap-2">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center w-11 h-11 rounded-full bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground transition-colors hover:bg-primary-foreground/20"
                aria-label={social.name}
              >
                <social.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
